import { useEffect, type PropsWithChildren } from "react";
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from "react-oidc-context";
import { oidcConfig, OIDC_ENABLED } from "@/shared/auth/oidcConfig";
import {
  ACCESS_DENIED_ENTRY,
  clearReturningUser,
  markReturningUser,
} from "@/shared/auth/returningUser";
import { useLatestRef } from "@/shared/hooks/useLatestRef";
import {
  setAccessTokenResolver,
  setAccessTokenRenewer,
  setSessionInvalidationHandler,
} from "@/services/authBridge";

/**
 * Removes the `?code=...&state=...` callback params from the URL after a
 * successful sign-in so they aren't reprocessed on reload and silent renewal
 * works correctly. Required by react-oidc-context.
 */
function onSigninCallback(): void {
  globalThis.window?.history.replaceState({}, "", globalThis.window.location.pathname);
}

/**
 * Bridges the OIDC client out to the transport layer, which runs outside React:
 *  - `apiFetch` reads the current access token through the token resolver.
 *  - On a 401, `apiFetch` first asks the token renewer (`signinSilent`) for a fresh
 *    token and retries the request once. Only if that renewal fails (or the retried
 *    request is still 401) does it clear the local session and return to the login
 *    route. The disabled-account 403 skips renewal and invalidates immediately.
 */
function AuthBridge({ children }: Readonly<PropsWithChildren>) {
  // oidc-client-ts returns stable, already-bound methods; `this` is never rebound.
  // oxlint-disable-next-line typescript/unbound-method
  const { user, removeUser, signinSilent } = useOidcAuth();

  // Hold the live token in a ref updated during render. The resolver is
  // registered once and reads the ref at request time, so it never closes over
  // a stale token. This matters on reload: React flushes descendant effects
  // (e.g. the first `/api/v1/me` query) before this provider's effects, so a
  // per-render closure could send the first authenticated request with the
  // previous (null) token, hit a 401, and force a sign-out. Writing the ref in
  // the render that authenticates guarantees the token is present before any
  // descendant effect fires.
  const accessTokenRef = useLatestRef<string | null>(user?.access_token ?? null);

  // Remember that this browser has signed in at least once, so the entry route
  // can auto-redirect returning users (whose ~24h Entra refresh token expired)
  // straight back to the IdP instead of showing a manual "Sign in" button.
  // First-time visitors never get the flag, so they still see the landing page.
  useEffect(() => {
    if (user?.access_token) {
      markReturningUser();
    }
  }, [user?.access_token]);

  useEffect(() => {
    setAccessTokenResolver(() => accessTokenRef.current);
    return () => setAccessTokenResolver(null);
    // Stable ref. The resolver is registered once and reads the token at request time.
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  // Keep the latest signinSilent in a ref so the renewer is registered once and never
  // closes over a stale callback. It returns the freshly minted access token directly
  // (rather than reading accessTokenRef, which lags one React render behind) so the
  // 401 retry uses the new token immediately.
  const signinSilentRef = useLatestRef(signinSilent);

  useEffect(() => {
    setAccessTokenRenewer(async () => {
      try {
        const renewedUser = await signinSilentRef.current();
        return renewedUser?.access_token ?? null;
      } catch (err) {
        console.warn("[auth] silent token renewal failed", err);
        return null;
      }
    });
    return () => setAccessTokenRenewer(null);
    // Stable ref. The renewer is registered once and calls the latest signinSilent.
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  useEffect(() => {
    setSessionInvalidationHandler((reason) => {
      // A revoked account still has a live Entra session, so the entry route's
      // returning-user redirect would sign it straight back in and loop. Drop the
      // flag and land on the no-access notice instead.
      const accessRevoked = reason === "accessRevoked";
      if (accessRevoked) {
        clearReturningUser();
      }

      // Clear the stored session BEFORE reloading so the app can't rehydrate a
      // dead session on the way back to the login route. removeUser() resolves
      // after it has cleared storage; navigate on either outcome.
      const toLogin = () =>
        globalThis.window?.location.assign(accessRevoked ? ACCESS_DENIED_ENTRY : "/");
      removeUser().then(toLogin, toLogin);
    });
    return () => setSessionInvalidationHandler(null);
  }, [removeUser]);

  return <>{children}</>;
}

export default function AuthProvider({ children }: Readonly<PropsWithChildren>) {
  useEffect(() => {
    if (!OIDC_ENABLED) {
      console.warn(
        "[auth] OIDC is not configured (OIDC_AUTHORITY / OIDC_CLIENT_ID missing); " +
          "sign-in will not work.",
      );
    }
  }, []);

  return (
    <OidcAuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
      <AuthBridge>{children}</AuthBridge>
    </OidcAuthProvider>
  );
}
